import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Globe, Users, ChevronDown, ChevronUp, Check } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors } from './matchPickStats.helper';
import { getPickStatsScopeToggleStyles } from './PickStatsScopeToggle.styles';
import { MATCH_PICK_STATS_COPY } from './MatchPickStats.constants';
import type { PickStatsScope } from '../../../src/worldcup/hooks/useMatchPickStats';
import type { GroupsApiEntry } from '../../../src/worldcup/hooks/useWorldCupGroupsQuery';

interface PickStatsScopeToggleProps {
  scope: PickStatsScope;
  groups: GroupsApiEntry[];
  selectedGroupId: string | null;
  onScopeChange: (scope: PickStatsScope) => void;
  onGroupChange: (groupId: string) => void;
}

export const PickStatsScopeToggle: React.FC<PickStatsScopeToggleProps> = ({
  scope,
  groups,
  selectedGroupId,
  onScopeChange,
  onGroupChange,
}) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const styles = useMemo(() => getPickStatsScopeToggleStyles(C), [C]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const activeGroupId = selectedGroupId ?? groups[0]?.id ?? null;
  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? groups[0] ?? null;

  const handleGroupsTab = useCallback(() => {
    onScopeChange('group');
  }, [onScopeChange]);

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen((v) => !v);
  }, []);

  const handleGroupSelect = useCallback(
    (id: string) => {
      onGroupChange(id);
      setDropdownOpen(false);
    },
    [onGroupChange],
  );

  return (
    <View style={styles.scopeWrap}>
      <View style={styles.scopeToggle}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ selected: scope === 'worldwide' }}
          onPress={() => { onScopeChange('worldwide'); setDropdownOpen(false); }}
          style={({ pressed }) => [
            styles.scopeTab,
            scope === 'worldwide' && styles.scopeTabActive,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.scopeBtnPressed,
          ]}
        >
          <Globe size={14} color={scope === 'worldwide' ? C.textOnPrimary : C.inkMute} strokeWidth={2.5} />
          <Text style={[styles.scopeTabText, scope === 'worldwide' && styles.scopeTabTextActive]}>
            {MATCH_PICK_STATS_COPY.SCOPE_WORLDWIDE}
          </Text>
        </Pressable>

        {groups.length > 0 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected: scope === 'group' }}
            onPress={handleGroupsTab}
            style={({ pressed }) => [
              styles.scopeTab,
              scope === 'group' && styles.scopeTabActive,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.scopeBtnPressed,
            ]}
          >
            <Users size={14} color={scope === 'group' ? C.textOnPrimary : C.inkMute} strokeWidth={2.5} />
            <Text style={[styles.scopeTabText, scope === 'group' && styles.scopeTabTextActive]}>
              {MATCH_PICK_STATS_COPY.SCOPE_GROUP}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {scope === 'group' && groups.length > 1 && activeGroup ? (
        <View style={styles.groupDropdownWrap}>
          <Pressable
            onPress={handleDropdownToggle}
            style={({ pressed }) => [
              styles.groupDropdownTrigger,
              dropdownOpen && styles.groupDropdownTriggerOpen,
              pressed && styles.scopeBtnPressed,
            ]}
          >
            <View style={styles.groupDropdownTriggerInner}>
              <View style={styles.groupDropdownIcon}>
                <Users size={12} color={C.purple} strokeWidth={2.5} />
              </View>
              <View style={styles.groupDropdownTextContent}>
                <Text style={styles.groupDropdownName} numberOfLines={1}>
                  {activeGroup.name}
                </Text>
                <Text style={styles.groupDropdownMeta}>
                  {activeGroup.memberCount} members · #{activeGroup.userRank}
                </Text>
              </View>
              {dropdownOpen
                ? <ChevronUp size={14} color={C.inkDim} />
                : <ChevronDown size={14} color={C.inkDim} />}
            </View>
          </Pressable>

          {dropdownOpen ? (
            <View style={styles.groupDropdownList}>
              {groups.map((g, idx) => {
                const isActive = g.id === activeGroupId;
                const isLast = idx === groups.length - 1;
                return (
                  <Pressable
                    key={g.id}
                    onPress={() => handleGroupSelect(g.id)}
                    style={({ pressed }) => [
                      styles.groupDropdownItem,
                      !isLast && styles.groupDropdownItemBorder,
                      pressed && styles.scopeBtnPressed,
                    ]}
                  >
                    <View style={styles.groupDropdownItemTextContent}>
                      <Text style={[styles.groupDropdownItemName, isActive && styles.groupDropdownItemNameActive]} numberOfLines={1}>
                        {g.name}
                      </Text>
                      <Text style={styles.groupDropdownItemMeta}>
                        {g.memberCount} members · #{g.userRank}
                      </Text>
                    </View>
                    {isActive ? (
                      <View style={styles.groupDropdownCheck}>
                        <Check size={10} color={C.bg} strokeWidth={3} />
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};
